import { lazy } from 'react';

import { IRoute } from '../../types/routes';

import { PendingDoubtsBadge } from '@/components/PendingDoubtsBadge';
import { PendingIncidentsBadge } from '@/components/PendingIncidentsBadge';
import { PendingMessagesBadge } from '@/components/PendingMessagesBadge';
import { PendingSupportsBadge } from '@/components/PendingSupportsBadge';

const TeacherDashboardPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.DashboardPage }))
);

const TicketsPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.TicketsPage }))
);

const EventsPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.EventsPage }))
);

const EventPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.EventPage }))
);

const CoursePage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.CoursePage }))
);

const CoursesPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.CoursesPage }))
);

const QuizzesPage = lazy(() =>
  import('@/pages/Manager').then(module => ({ default: module.QuizzesPage }))
);

const QuestionBankPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.QuestionBankPage }))
);

const QuizFormPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.QuizFormPage }))
);

const TutoringGroupsPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.TutoringGroupsPage }))
);

const TutoringGroupPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.TutoringGroupPage }))
);

const ProblemReportsPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.ProblemReportsPage }))
);

const ChatPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.ChatPage }))
);

const ExamGroupsPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.ExamGroupsPage }))
);

const StudentsPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.StudentsPage }))
);

const ProfilePage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.ProfilePage }))
);

const ExamsPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.ExamsPage }))
);

const ExamDetailPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.ExamDetailPage }))
);

const DeliverablesPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.DeliverablesPage }))
);

const LiveClassPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.LiveClassPage }))
);

const TaskPanelPage = lazy(() =>
  import('@/pages/Teacher').then(module => ({ default: module.TaskPanelPage }))
);

export const TEACHER: IRoute[] = [
  {
    title: 'DASHBOARD',
    path: '/dashboard',
    icon: 'Home',
    Component: TeacherDashboardPage,
    showInMenu: true
  },
  {
    title: 'STUDENTS',
    path: '/students',
    icon: 'UserMultiple',
    Component: StudentsPage,
    showInMenu: true
  },
  {
    title: 'TASKS_MANAGEMENT',
    path: '/task-panel',
    icon: 'TaskComplete',
    Component: TaskPanelPage,
    showInMenu: true
  },
  {
    title: 'FORMATION',
    path: '/content',
    icon: 'Notebook',
    Component: CoursesPage,
    showInMenu: true,
    subroutes: [
      {
        title: 'COURSES',
        path: 'courses',
        icon: 'Undefined',
        Component: CoursesPage,
        showInMenu: true,
        subroutes: [
          {
            title: 'COURSES',
            path: ':_id',
            icon: 'Undefined',
            Component: CoursePage,
            showInMenu: false
          }
        ]
      },
      {
        title: 'LIVE_EVENTS',
        path: 'calendar',
        icon: 'EventSchedule',
        Component: EventsPage,
        showInMenu: true,
        subroutes: [
          {
            title: 'LIVE_EVENTS',
            path: ':_id',
            icon: 'Undefined',
            Component: EventPage,
            showInMenu: false
          }
        ]
      }
    ]
  },

  {
    title: 'EVALUATION',
    path: '/content',
    icon: 'RuleTest',
    Component: ExamsPage,
    showInMenu: true,
    subroutes: [
      {
        title: 'TESTS',
        path: 'tests',
        icon: 'TestTool',
        Component: QuizzesPage,
        showInMenu: true,
        subroutes: [
          {
            title: 'TESTS',
            path: ':_id/edit',
            icon: 'Undefined',
            Component: QuizFormPage,
            showInMenu: false
          },
          {
            title: 'CREATE_TEST',
            path: 'create',
            icon: 'Undefined',
            Component: QuizFormPage,
            showInMenu: false
          },
          {
            title: 'TESTS',
            path: 'import',
            icon: 'Undefined',
            Component: QuizFormPage,
            showInMenu: false
          },
          {
            title: 'QUESTION_BANK',
            path: 'question-bank',
            icon: 'Undefined',
            Component: QuestionBankPage,
            showInMenu: false
          }
        ]
      },
      {
        title: 'EXAMS',
        path: 'exams',
        icon: 'DocumentRequirements',
        Component: ExamsPage,
        showInMenu: true,
        subroutes: [
          {
            title: 'EXAMS',
            path: ':_id',
            icon: 'Undefined',
            Component: ExamDetailPage,
            showInMenu: false
          }
        ]
      },
      {
        title: 'DELIVERABLES',
        path: 'deliverables',
        icon: 'DocumentExport',
        Component: DeliverablesPage,
        showInMenu: true
      }
    ]
  },

  {
    title: 'SUPPORT',
    path: '/communication',
    icon: 'ChatLaunch',
    Component: TicketsPage,
    TitleAddon: PendingSupportsBadge,
    showInMenu: true,
    subroutes: [
      {
        title: 'DOUBTS',
        path: 'tickets',
        icon: 'MailAll',
        Component: TicketsPage,
        TitleAddon: PendingDoubtsBadge,
        showInMenu: true
      },
      {
        title: 'TUTORING_GROUPS',
        path: 'tutoring-groups',
        icon: 'UserMultiple',
        Component: TutoringGroupsPage,
        showInMenu: true,
        TitleAddon: PendingMessagesBadge,
        subroutes: [
          {
            title: 'TUTORING_GROUPS',
            path: ':tutoringGroupId',
            icon: 'Undefined',
            Component: TutoringGroupPage,
            showInMenu: false
          },
          {
            title: 'TUTORING_GROUPS',
            path: ':tutoringGroupId/chat/:tutorId/:studentId',
            icon: 'Undefined',
            Component: ChatPage,
            showInMenu: false
          }
        ]
      },
      {
        title: 'INCIDENTS',
        path: 'incidents',
        icon: 'Undefined',
        Component: ProblemReportsPage,
        TitleAddon: PendingIncidentsBadge,
        showInMenu: true
      }
    ]
  },
  {
    title: 'EXAM_GROUPS',
    path: '/exam-groups',
    icon: 'Event',
    Component: ExamGroupsPage,
    showInMenu: true
  },
  {
    title: 'PROFILE',
    path: '/profile',
    icon: 'Undefined',
    Component: ProfilePage,
    showInMenu: false
  },
  {
    title: 'LIVE_CLASS',
    path: '/live-class/:meetingId',
    icon: 'Undefined',
    Component: LiveClassPage,
    showInMenu: false
  }
];
